import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { useDateFormatters, DATE_FORMATS } from '../utils/dateHelpers';

/**
 * Custom hook for localized date formatting
 * Integrates with react-i18next for automatic language detection
 * 
 * @returns {Object} Object containing formatted date functions and format constants
 * 
 * @example
 * const { format, relative, dateTime, time, formats } = useDateFormat();
 * 
 * // Basic usage
 * format(new Date()) // "4 Nisan 2022" (in Turkish) or "April 4, 2022" (in English)
 * 
 * // With custom format
 * format(new Date(), formats.SHORT) // "04.04.2022" (in Turkish) or "04/04/2022" (in English)
 * 
 * // Relative time
 * relative(new Date()) // "şimdi" (in Turkish) or "now" (in English)
 */
export const useDateFormat = () => {
  const { t, i18n } = useTranslation();
  
  // Get date formatters with current language
  const dateFormatters = useDateFormatters(i18n);
  
  // Optimized formatters with useMemo to prevent recreation on every render
  const formatters = useMemo(() => ({
    /**
     * Format date with specified format or default (LONG) format
     * @param {string|Date} date - Date to format
     * @param {string} [format=DATE_FORMATS.LONG] - Date format pattern
     * @param {string} [fallbackKey='common.notAvailable'] - Translation key for fallback text
     * @returns {string} Formatted date
     */
    format: (date, format = DATE_FORMATS.LONG, fallbackKey = 'common.notAvailable') =>
      dateFormatters.formatDate(date, format, t(fallbackKey)),
    
    /**
     * Format date as relative time (e.g., "2 hours ago")
     * @param {string|Date} date - Date to format
     * @param {string} [fallbackKey='common.unknown'] - Translation key for fallback text
     * @returns {string} Relative time string
     */
    relative: (date, fallbackKey = 'common.unknown') => 
      dateFormatters.formatRelative(date, t(fallbackKey)),
    
    /**
     * Format date with time
     * @param {string|Date} date - Date to format
     * @param {string} [fallbackKey='common.notAvailable'] - Translation key for fallback text
     * @returns {string} Date with time
     */
    dateTime: (date, fallbackKey = 'common.notAvailable') => 
      dateFormatters.formatDateTime(date, t(fallbackKey)),
    
    /**
     * Format only time part
     * @param {string|Date} date - Date to format
     * @param {string} [fallbackKey='common.notAvailable'] - Translation key for fallback text
     * @returns {string} Time string
     */
    time: (date, fallbackKey = 'common.notAvailable') => 
      dateFormatters.formatTime(date, t(fallbackKey))
  }), [dateFormatters, t]); // Only recreate when dateFormatters or t function changes
  
  // Memoize the entire return object to prevent unnecessary re-renders
  return useMemo(() => ({
    // Simplified and optimized formatters
    ...formatters,
    
    // Backward compatibility - deprecated but still available
    formatDate: formatters.format,
    formatDateTime: formatters.dateTime,
    formatRelative: formatters.relative,
    formatTime: formatters.time,
    formatShort: (date, fallbackKey = 'common.notAvailable') => 
      dateFormatters.formatShort(date, t(fallbackKey)),
    formatLong: (date, fallbackKey = 'common.notAvailable') => 
      dateFormatters.formatLong(date, t(fallbackKey)),
    
    // Format constants
    formats: DATE_FORMATS,
    
    // Current language info for debugging/special cases
    language: i18n.language
  }), [formatters, dateFormatters, t, i18n.language]);
};

export default useDateFormat; 